/**
 * Member-facing tone + label for a Command Center status
 * (STARSEED-CAPMARKETS-140).
 *
 * Extracted from CommandCenterPage so the mapping is testable and so adding a
 * status is a one-line change in one place — the same remedy CAPMARKETS-120
 * applied to the membership-payment badge, for the same class of bug.
 *
 * The bug this fixes: the map named the LEGACY ladder only. Every one of the
 * eleven FinBPM statuses fell through `STATUS_TONE[status] || {tone:"slate",
 * label: status}`, so a fully approved member's badge rendered
 *
 *     slate/grey  "FinBPM Approved"
 *
 * — the same grey as "Closed", carrying a raw internal status string. Two
 * defects in one fallback:
 *
 *   1. TONE. Grey is the tone this app uses for terminal-neutral outcomes
 *      (Draft, Closed). An approved member whose funding is in progress read
 *      as though nothing good had happened, on the same page CAPMARKETS-117
 *      had just been fixed on.
 *   2. LABEL. `label: status` leaks the vendor name. Member-visible copy is
 *      vendor-neutral by doctrine — `models/command_center.py` deliberately
 *      labels STATUS_FINBPM_MONAXA_CREATED "Partner Account Created" for
 *      exactly this reason, and `_next_action_for_status` says "the partner"
 *      throughout. The fallback bypassed all of that and showed members
 *      "FinBPM Approved" / "FinBPM Agreement Sent" / "FinBPM Rejected".
 *
 * Tones follow the convention already in the map: amber = the member has
 * something to do, blue = someone else is working, emerald = a milestone is
 * banked, slate = terminal-neutral, red = alert. They are aligned with
 * `blocked_on` in `services/command_center_engine.py::_next_action_for_status`
 * so the badge colour and the next-action copy cannot disagree.
 */

/**
 * Status → badge tone + member-facing label.
 *
 * Keys are the exact status strings emitted by
 * `apps/capital-markets-api/models/command_center.py`. The companion test
 * parses that file and fails if any status here is missing an entry — the
 * cross-language guard that would have caught this bug, since the two ladders
 * live in different languages and nothing else forces them to agree.
 */
export const STATUS_TONE = {
  // --- alerts / holds -----------------------------------------------------
  "Red Alert":                          { tone: "red",     label: "Red Alert" },
  "Compliance Attention Required":      { tone: "red",     label: "Compliance Attention" },
  "Priority Review":                    { tone: "amber",   label: "Priority Review" },
  "Suspended / Review Pending":         { tone: "amber",   label: "Paused / Review" },
  "Rejected / Closed":                  { tone: "slate",   label: "Closed" },
  "More Information Required":          { tone: "amber",   label: "More Info Required" },

  // --- intake / verification ----------------------------------------------
  "Draft":                              { tone: "slate",   label: "Draft" },
  "Profile Submitted":                  { tone: "blue",    label: "Profile Submitted" },
  "KYC Pending":                        { tone: "blue",    label: "KYC In Progress" },
  "KYC Complete":                       { tone: "emerald", label: "KYC Complete" },
  "Accreditation Pending":              { tone: "blue",    label: "Accreditation In Progress" },
  "Accreditation Complete":             { tone: "emerald", label: "Accreditation Complete" },
  "Data Transfer Consent Required":     { tone: "amber",   label: "Consent Required" },

  // --- legacy partner ladder ----------------------------------------------
  "Ready for Regulated Partner Review": { tone: "emerald", label: "Ready for Partner" },
  "Transferred to Regulated Partner":   { tone: "emerald", label: "Transferred to Partner" },
  "Partner Onboarding Pending":         { tone: "blue",    label: "Partner Onboarding" },
  "Partner Onboarding Complete":        { tone: "emerald", label: "Partner Onboarded" },

  // --- FinBPM partner ladder (capmarkets-140) -----------------------------
  // Labels are vendor-neutral: the member sees "the partner", never "FinBPM".
  "FinBPM Agreement Sent":              { tone: "amber",   label: "Agreement Ready to Sign" },
  "Investor Signed":                    { tone: "blue",    label: "Signed — With Partner" },
  "Pending FinBPM Review":              { tone: "blue",    label: "Partner Review In Progress" },
  "FinBPM Approved":                    { tone: "emerald", label: "Agreement Approved" },
  "FinBPM Rejected / More Info Needed": { tone: "amber",   label: "More Information Needed" },
  "Funding Instructions Sent":          { tone: "amber",   label: "Funding Instructions Ready" },
  "Proof of Payment Uploaded":          { tone: "blue",    label: "Payment Under Review" },
  "Funds Confirmed":                    { tone: "emerald", label: "Funds Confirmed" },
  "Partner Account Created":            { tone: "emerald", label: "Partner Account Created" },
  "Dashboard Activated":                { tone: "emerald", label: "Dashboard Active" },
  // Voided, not failed: the member executes a corrected agreement next. Slate
  // rather than red — nothing has gone wrong on their side.
  "Superseded / Void":                  { tone: "slate",   label: "Agreement Voided" },
};

export const TONE_CLASSES = {
  red:     "bg-red-50  border-red-300  text-red-900",
  amber:   "bg-amber-50 border-amber-300 text-amber-900",
  blue:    "bg-blue-50  border-blue-300  text-blue-900",
  emerald: "bg-emerald-50 border-emerald-300 text-emerald-900",
  slate:   "bg-slate-100 border-slate-300 text-slate-900",
};

/**
 * Resolve a status to its badge tone + label.
 *
 * An unrecognised status still falls back to neutral-slate, but the label
 * falls back to a generic "In Progress" rather than echoing the raw status
 * string — an unmapped status must never again put internal vocabulary in
 * front of a member. The exhaustiveness test is what keeps the fallback
 * unreachable for statuses the API can actually emit.
 *
 * @param {string|undefined|null} status
 * @returns {{tone: string, label: string}}
 */
export function statusTone(status) {
  const key = String(status || "").trim();
  return STATUS_TONE[key] || { tone: "slate", label: "In Progress" };
}

/**
 * Tailwind classes for a resolved tone, defaulting to slate for an unknown
 * tone name.
 *
 * @param {string} tone
 * @returns {string}
 */
export function toneClasses(tone) {
  return TONE_CLASSES[tone] || TONE_CLASSES.slate;
}

/**
 * STARSEED-CAPMARKETS-257 — the checklist item that is WITH STAFF in a given
 * status: the one open item the member cannot act on. The dashboard's "next
 * step" skips it, so the dashboard and the Command Center — whose
 * `next_action_url` is null for these statuses and which prints "no action
 * required" — flip together, because both derive from `_derive_status` on
 * the same request. Keyed on the payload's `status` rather than on
 * `user.kyc_status` because the engine's predicate needs fields the client is
 * never sent (`kyc_started_at` / `kyc_provider`), and `accredited_status` is
 * not on the auth payload at all.
 *
 * Keys are engine status strings (the STATUS_TONE keys above); values are
 * checklist keys from `services/onboarding_checklist.py`.
 */
export const CHECKLIST_ITEM_WITH_STAFF = {
  "KYC Pending":           "kyc",
  "Accreditation Pending": "accredited",
};

export function checklistItemWithStaff(status) {
  return CHECKLIST_ITEM_WITH_STAFF[String(status || "").trim()] || null;
}

/**
 * STARSEED-CAPMARKETS-257 — statuses whose own next-action copy tells the
 * member to STOP, not merely to wait. When the payload carries no action of
 * the member's, the dashboard does not fall back to a checklist item here:
 * the Command Center is the call to action, and it explains why.
 *
 * The membership is the engine's own wording (`_next_action_for_status`),
 * not the badge's red/amber block:
 *   - Red Alert — "do not send funds or take any further action"
 *   - Compliance Attention Required — "before any further step can be taken"
 *   - Suspended / Review Pending — "Your file is currently paused"
 *   - Rejected / Closed — "This file has been closed" (it carries a
 *     `/contact` url, so it only reaches this set once a reopen request has
 *     nulled the url; then the Command Center entry is right)
 * `Priority Review` is deliberately NOT here: `priority_level == "priority"`
 * is a sticky expedite flag an admin — or the member's own escalation — can
 * set at any stage and nothing clears, its copy says "we will contact you
 * with next steps", and silencing the call to action for the members being
 * handled fastest is the 2026-09-08 failure again. `More Information
 * Required` is not here either: it asks the member for something and carries
 * a url.
 */
export const CHECKLIST_HOLD_STATUSES = new Set([
  "Red Alert",
  "Compliance Attention Required",
  "Suspended / Review Pending",
  "Rejected / Closed",
]);

export function checklistOnHold(status) {
  return CHECKLIST_HOLD_STATUSES.has(String(status || "").trim());
}
